import {
  Alert,
  AlertTitle,
  Box,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Paper,
  RadioGroup,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

import styled, { css, DefaultTheme } from "styled-components";

import {
  FieldContainer,
  FieldTitle,
} from "../shared/TextField/FormField.styles";
import { TextField, TextFieldProps } from "../shared/TextField/TextField";
import Illustration from "../../assets/images/Illustration.svg";
import { StyledButton } from "../shared/Button/Button.styles";
import { DeviceType } from "../../utils/hooks/useDeviceHook";
import { UserReportGenerateNotesModalNPIMessage } from "../UserReport/UserReport.styles";
import { SpinnerWrapper } from "../shared/Spinner/SpinnerLoading.styles";
import { testOrderSecondaryCss } from "../shared/TestOrderDisplay.styles";

const requisitionFieldErrorStyles = css`
  .Mui-error.MuiInputLabel-root {
    color: ${({ theme }) => theme.redDark} !important;
  }
  .Mui-error.MuiInputLabel-asterisk {
    color: ${({ theme }) => theme.redDark} !important;
  }
  .Mui-error {
    .MuiInput-input {
      color: ${({ theme }) => theme.redDark} !important;
    }
    :after {
      border-bottom: 1px solid ${({ theme }) => theme.redDark} !important;
    }
  }
`;

export const PreFlightWrapper = styled.div`
  && {
    .gender-form-control {
      border-bottom: ${({ theme }) =>
        `1px dashed ${theme.greyLight}`} !important;
    }

    .MuiInput-root:after {
      border-bottom: ${({ theme }) =>
        `1px dashed ${theme.greyLight}`} !important;
    }
    .MuiInput-root:before {
      border-bottom: ${({ theme }) =>
        `1px dashed ${theme.greyLight}`} !important;
    }
  }
  pointer-events: none;
  width: 100%;
  display: relative;
`;

export const PatientInformationBox = styled(Box)`
  && {
    .Mui-focused {
      color: ${({ theme }) => theme.black};
    }

    min-width: 120px;
    color: ${({ theme }) => theme.black};
    font-family: light;

    .MuiInput-input {
      font-family: light;
      font-size: 1rem;
    }

    .MuiAutocomplete-input {
      color: ${({ theme }) => theme.black} !important;
      font-family: light;
      font-size: 1rem;
    }

    .MuiInputLabel-root {
      color: ${({ theme }) => theme.blueDark};
      font-family: light;
      line-height: inherit;
    }

    .MuiInput-root:after {
      border-bottom: ${({ theme }) => `1px solid ${theme.greyLight}`};
    }

    ${requisitionFieldErrorStyles}
  }
  width: 100%;
`;

export const PaperWrapper = styled(Paper)`
  display: flex;
  align-items: center;
  height: 64px;
  background-color: inherit;
`;

export const StyledFormLabel = styled(FormLabel)`
  color: ${({ theme, error }) =>
    error ? theme.redDark : theme.neutral700} !important;
  font-size: 18px;
  font-family: bold !important;
  text-transform: uppercase;
  margin: 24px 0 16px 0;
`;

export const StepperPanelDesktop = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 20px 24px 20px;
  background-color: ${({ theme }) => theme.neutral50};
  border-top: 1px solid ${({ theme }) => theme.neutral100};
  bottom: 0;
  left: 0;
  width: calc(100% - 42px);

  .MuiStepConnector-horizontal.Mui-active .MuiStepConnector-lineHorizontal {
    border-color: ${({ theme }) => theme.redMedium};
    border-top-width: 2px;
  }

  ${StyledButton} {
    height: 48px !important;
  }

  ${SpinnerWrapper} {
    margin: 0px;
    margin-right: 10px;
  }

  .save-draft-button {
    margin-right: 15px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export const StepperPanelMobile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: space-between;
  padding: 16px 10px 24px 10px;
  background-color: ${({ theme }) => theme.neutral50};
  border-top: 1px solid ${({ theme }) => theme.neutral100};
  width: calc(100% - 32px);

  @media (min-width: 768px) {
    display: none;
  }

  ${StyledButton} {
    height: 48px;
  }

  .MuiStepConnector-horizontal.Mui-active .MuiStepConnector-lineHorizontal {
    border-color: ${({ theme }) => theme.redMedium};
    border-top-width: 2px;
  }
`;

export const StepperButtonMobile = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const EditOrderDialogTitle = styled(DialogTitle)`
  && {
    margin: 0;
    padding: 16px;
    background-color: ${({ theme }) => theme.neutral50};
    border-bottom: 1px solid ${({ theme }) => theme.neutral100};
  }
`;

export const EditOrderHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-right: 32px;
`;

export const EditOrderCountdownPill = styled.span`
  border: 1px solid ${({ theme }) => theme.greyLight};
  border-radius: 12px;
  color: ${({ theme }) => theme.greyDark};
  font-family: light;
  font-size: 14px;
  line-height: 20px;
  padding: 2px 8px;

  strong {
    font-family: bold;
  }
`;

export const EditOrderCloseButton = styled(IconButton)`
  && {
    position: absolute;
    right: 8px;
    top: 8px;
    color: ${({ theme }) => theme.redMedium};
  }
  &&.Mui-disabled {
    opacity: 0.5;
  }
`;

export const EditOrderDialogContent = styled(DialogContent)`
  && {
    overflow-x: hidden;
  }
`;

export const NewReqStepIconActive = styled(RadioButtonUncheckedIcon)`
  color: ${({ theme }) => theme.redMedium};
`;

export const NewReqStepIconCompleted = styled(CheckCircleIcon)`
  color: ${({ theme }) => theme.redMedium};
`;

export const NewReqStepIconIdle = styled(RadioButtonUncheckedIcon)`
  color: ${({ theme }) => theme.greyLight};
`;

export const EditOrderSaveButtonSlot = styled.div`
  flex: 0 0 215px;

  ${StyledButton} {
    width: 215px;
  }
`;

export const StepperWrapper = styled(Stepper)`
  flex: 2;
  max-width: 250px;
  margin: auto;

  .MuiStep-root {
    padding-right: 0px !important;
  }
`;

export const NewReqDraftDelete = styled.div`
  width: 50px;
  height: 46px;
  border-radius: 30px;
  justify-content: center;
  border: 2px solid var(--Secondary-Red-Medium, #f0604b);
  /* padding: 16px; */
  background: white;
  display: flex;
  align-items: center;
  margin-right: 15px;
  cursor: pointer;
  svg {
    height: 20px;
    width: 20px;
  }
`;

export const NewRequisitionTextFieldLabel = styled.div`
  svg {
    width: 20px;
    margin-bottom: -4px;
  }
`;

export const PolicyOwnerRelationshipText = styled.div`
  p {
    font-family: light !important;
    font-size: 12px !important;
  }
`;

const getDateColor = (
  theme: DefaultTheme,
  error?: string,
  noDateValue?: boolean,
) => {
  if (error) return theme.redDark;
  if (noDateValue) return theme.neutral500;
  return theme.black;
};

// Keep the label above the input when a field is disabled, as when focused.
export const NewRequisitionTextField = styled(TextField).attrs(
  ({ InputLabelProps }: TextFieldProps): Partial<TextFieldProps> => ({
    InputLabelProps: { shrink: true, ...InputLabelProps },
  }),
)<{
  $noDateValue?: boolean;
  $showHelpertext?: boolean;
}>`
  color: ${({ theme }) => theme.greyLight};
  width: 100%;
  font-family: light;

  input::-webkit-datetime-edit {
    color: ${({ theme, error, $noDateValue }) =>
      getDateColor(theme, error, $noDateValue)};
  }

  .MuiFormHelperText-root {
    display: ${({ $showHelpertext }) => ($showHelpertext ? undefined : "none")};
    color: ${({ theme }) => theme.blueDark} !important;
  }

  .MuiFormHelperText-root.Mui-error {
    display: ${({ $showHelpertext }) => ($showHelpertext ? undefined : "none")};
    color: ${({ $showHelpertext, theme }) =>
      $showHelpertext ? theme.redDark : undefined} !important;
  }
`;

export const NewReqStateGrid = styled(Grid)`
  ${FieldContainer} {
    width: 100%;
  }
  .MuiInput-input {
    padding: 0px 4px 4px 0px !important;
  }
  ${FieldTitle} {
    margin-top: -5px;
    margin-bottom: 5px;
  }
`;

export const NewReqStreetGrid = styled(Grid)<{ $addPadding: boolean }>`
  ${FieldContainer} {
    width: 100%;
  }
  ${FieldTitle} {
    margin-top: -5px;
    margin-bottom: 5px;
  }
  .MuiInput-input {
    padding: ${({ $addPadding }) =>
      $addPadding ? "0px 4px 4px 0px" : undefined} !important;
  }

  svg {
    display: none;
  }
`;

export const CheckboxWrapper = styled(FormControlLabel)`
  background-color: ${({ theme }) => theme.neutral50};
  width: 100%;
  height: 100%;
  border-radius: 6px;

  && {
    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
  }
`;

export const NewReqFetalAntigenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  gap: 0 16px;
  width: 100%;
`;

export const FetalAntigenNestedOptions = styled.div<{
  $topMargin: boolean;
}>`
  margin-left: 28px;
  margin-top: ${({ $topMargin }) => ($topMargin ? "8px" : 0)};
`;

export const PlateletAntigensToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
  color: ${({ theme }) => theme.blueDark};
  font-size: 14px;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }

  svg {
    transition: transform 0.2s ease;
    transform: rotate(180deg);
  }

  &[data-expanded="true"] svg {
    transform: rotate(0deg);
  }
`;

export const PlateletAntigensHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

export const CollapsiblePlateletGrid = styled.div<{ isExpanded: boolean }>`
  display: grid;
  grid-template-columns: repeat(4, minmax(100px, 1fr));
  gap: 0 16px;
  max-height: ${({ isExpanded }) => (isExpanded ? "500px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding-left: 10px;
  margin-left: -10px;
`;

export const NewReqFetalAntigenGridWrapper = styled.div<{
  deviceType: DeviceType;
  disabled: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: ${({ deviceType }) =>
    deviceType === "mobile" ? "20px" : "36px"};
  padding-right: 0;
  width: 100%;
  box-sizing: border-box;
  opacity: ${({ disabled }) => (disabled ? "0.5" : 1)};

  .MuiFormControlLabel-root {
    margin-left: -9px;
  }
`;

export const AneuploidyGridWrapper = styled.div<{
  disabled: boolean;
}>`
  opacity: ${({ disabled }) => (disabled ? "0.48" : 1)};
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

export const BillingCheckboxWrapper = styled(FormControlLabel)`
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;

  && {
    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
  }
`;

export const SoloCheckboxWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  align-self: flex-start;
  /* Align checkbox with the first label line (medium typography). */
  height: 24px;
  overflow: visible;

  && {
    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
  }
`;

export const RadioFormControl = styled(FormControl)<{ $hideBorder?: boolean }>`
  && {
    .MuiFormLabel-root {
      color: ${({ theme, error }) =>
        error ? theme.redDark : theme.blueDark} !important;
      font-family: light !important;
      line-height: inherit !important;
      font-size: 12px !important;
    }
    .MuiFormControlLabel-label {
      font-size: 14px;
    }
    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
    .MuiTypography-root {
      margin-left: -2px;
    }
    width: 100%;
    border-bottom: ${({ theme, $hideBorder }) =>
      !$hideBorder && `1px solid ${theme.neutral400}`};
  }

  .MuiRadio-root {
    color: ${({ theme, error }) =>
      error ? theme.redDark : theme.greyLight} !important;
  }
  .Mui-error {
    color: ${({ theme, error }) =>
      error ? theme.redDark : undefined} !important;
  }

  ${NewRequisitionTextFieldLabel} {
    svg {
      width: 15px;
      margin-bottom: -8px;
    }
  }
`;

export const BillingRadioControlDropdown = styled(RadioFormControl)`
  && {
    .MuiFormLabel-root {
      font-size: 16px !important;
    }
  }
`;

export const CheckboxLabelWrapper = styled(FormControlLabel)`
  height: fit-content;
  align-items: flex-start;
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;

  && {
    .MuiCheckbox-root {
      margin: -9px 3px -9px -9px;
      padding: 9px;
    }

    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
  }
`;

export const FormControlLabelWrapper = styled(FormControlLabel)`
  font-family: light !important;
  align-self: flex-start;
  width: fit-content;
  max-width: 100%;

  && {
    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
  }
`;

export const StyledFormLabelPatient = styled(FormLabel)`
  color: ${({ theme }) => theme.blueDark};
  font-family: light;
  line-height: inherit;
  font-size: 13px;
  padding: 22px 0 18px 0;
  display: block;

  && {
    .Mui-checked {
      color: ${({ theme }) => theme.redMedium} !important;
    }
  }

  .label_subtext {
    color: #939393;
    font-size: 13px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
  }
`;

export const PatientSubLabel = styled(FormLabel)`
  && {
    color: ${({ theme }) => theme.black};
    font-size: 15px;
    font-family: light;
  }
`;

// Read-only option labels and section titles turn grey, as in the report
// update design; text field labels keep their own disabled style.
export const MutedDisabledLabels = styled.div`
  && .MuiFormControlLabel-label.Mui-disabled,
  && .MuiFormControlLabel-label.Mui-disabled *,
  && .MuiFormLabel-root.Mui-disabled:not(.MuiInputLabel-root) {
    color: ${({ theme }) => theme.neutral500};
  }
`;

export const PatientInfoAlert = styled(Alert)<{ $isError: boolean }>`
  border-left: 3px solid;
  background-color: ${({ $isError, theme }) =>
    $isError ? theme.redPastel : theme.greenSubtle} !important;
  border-color: ${({ $isError, theme }) =>
    $isError ? theme.redDark : theme.greenDark} !important;
  margin-bottom: 4rem;
`;

export const AlertTitleWrapper = styled(AlertTitle)`
  min-height: 40px;
  color: ${({ theme }) => theme.black};
  font-family: light;
`;

export const BoxWrapper = styled(Box)`
  display: flex;
  align-items: center;
  padding-right: 1rem;
  flex-wrap: wrap;
`;

/** Label widths so helper text aligns across top-level (359) and nested (323) rows. */
const TEST_OPTION_LABEL_WIDTH = "359px";
const NESTED_TEST_OPTION_LABEL_WIDTH = "323px";

export const NewReqTestOptionRow = styled.div<{ $nested?: boolean }>`
  display: grid;
  grid-template-columns:
    ${({ $nested }) =>
      $nested ? NESTED_TEST_OPTION_LABEL_WIDTH : TEST_OPTION_LABEL_WIDTH}
    minmax(0, 1fr);
  align-items: start;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

export const NewReqTestOptionLabelColumn = styled.div`
  display: flex;
  align-items: flex-start;
  min-width: 0;
`;

export const NewReqCheckboxLabelRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

export const NewReqHelperTextBox = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  gap: 11px;
  cursor: default;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.greyMedium};
  }
`;

export const NewReqHelperTextLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const NewReqPatientTestPanelCheckboxTitleWrapper = styled.div`
  align-self: baseline;
`;

export const NewReqPatientTestPanelRhdOnlyCheckboxWrapper = styled.div<{
  deviceType: DeviceType;
}>`
  display: grid;
  grid-template-columns: 95%;
  align-items: center;
  width: ${({ deviceType }) => (deviceType === "desktop" ? "125%" : "100%")};
`;

export const PatientBillingBox = styled(BoxWrapper)`
  ${RadioFormControl} {
    border-bottom: none;
  }
`;

export const PatientBillingDateWrapper = styled.div`
  .MuiTextField-root {
    padding: 6px 0px !important;
  }
`;

export const NewReqCFModalTitle = styled.div`
  display: flex;
  height: 72px;
  align-items: center;
  background: #e7ebf2;
  box-sizing: border-box;
  padding-left: 25px;
  font-size: 20px;
  font-style: normal;
  font-weight: 800;
  line-height: 32px; /* 133.333% */
  letter-spacing: 1.2px;
  border-radius: 8px;
  border-bottom-right-radius: unset;
  border-bottom-left-radius: unset;
  svg {
    margin-left: 25px;
    cursor: pointer;
  }
`;

export const CFModalButtons = styled.div`
  display: flex;
  grid-gap: 8px;
  padding: 16px;
  height: 44px;
  gap: 24px;
  justify-content: center;

  button {
    background-color: ${({ theme }) => theme.redMedium}!important;
    color: ${({ theme }) => theme.white} !important;
    padding: 5px 20px !important;
    width: max-content;
  }

  .opt-out {
    color: ${({ theme }) => theme.redMedium}!important;
    background-color: ${({ theme }) => theme.white} !important;
    display: block;
    margin-top: -32px;
  }

  .spinner {
    width: 100%;
    margin-top: -20px;
  }
`;

export const NewReqCFModalBody = styled.div`
  display: grid;
  grid-gap: 24px;
  margin-top: 24px;
  margin-bottom: 24px;
`;

export const ClinicSpinnerWrapper = styled.div`
  height: 100%;
  width: 100%;
`;

export const ClinicOrderingProviderSection = styled(Grid)`
  && {
    margin-top: 8px;
    margin-bottom: 48px;
  }
`;

export const CarrierExpandedHelperBox = styled(Box)<{ $disabled?: boolean }>`
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

export const FetalAntigenIndentGrid = styled(Grid)`
  margin-left: 28px;
`;

export const FetalAntigenIndentUploadGrid = styled(Grid)`
  margin-left: 28px;
  margin-bottom: 8px;
`;

export const FetalAntigenPrintIconSlot = styled.div`
  padding-right: 12px;
`;

export const ProviderAuthCheckboxLabel = styled(CheckboxLabelWrapper)`
  && {
    margin-top: 12px;
    margin-left: 0;
  }
`;

export const UnityCompleteSelectRoot = styled(Box)`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const CarrierPanelStack = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CarrierExpandedPanelGroup = styled(Box)`
  display: flex;
  flex-direction: column;
  /* The final panel row owns its vertical padding; the helper follows it. */
`;

export const TestPanelSectionStack = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

export const TestPanelNestedStack = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PatientSexRadioGroup = styled(RadioGroup)`
  margin-top: -8px;
`;

export const PatientFieldOffsetTop = styled(NewRequisitionTextField)`
  && {
    margin-top: 8px;
  }
`;

export const AbnormalUltrasoundField = styled(PatientFieldOffsetTop)`
  && {
    margin-top: 16px;
  }
`;

export const PatientGestationLabel = styled(StyledFormLabelPatient)`
  && {
    font-family: light;
  }
`;

export const StyledFormLabelClinical = styled(FormLabel)`
  color: ${({ theme }) => theme.blueDark};
  font-family: "light";
  font-size: 15px;
`;

/** Section headings (Unity Complete / Carrier / Aneuploidy). */
export const StyledSectionHeadingClinical = styled(FormLabel)<{
  $color?: string;
  $mb?: string;
  $mt?: string;
}>`
  && {
    display: block;
    font-family: bold;
    font-weight: 700;
    font-size: 18px;
    line-height: 28px;
    color: ${({ theme, $color }) => $color ?? theme.greyHeading};
    margin-bottom: ${({ $mb }) => $mb ?? "16px"};
    margin-top: ${({ $mt }) => $mt ?? "0"};
  }
`;

export const ClinicalIndicationHeadingSection = styled.div`
  margin: 24px;
  margin-bottom: 32px;
`;

export const ClinicalIndicationDashedDivider = styled(Divider)`
  && {
    margin-top: 32px;
    margin-bottom: 24px;
    border-style: dashed;
    border-color: ${({ theme }) => theme.greyLight};
  }
`;

export const ClinicalIndicationIcdDivider = styled(Divider)`
  && {
    margin-top: 24px;
    margin-bottom: 32px;
    border-style: dashed;
    border-color: ${({ theme }) => theme.greyLight};
  }
`;

export const StyledFormPrimaryLabelClinical = styled(FormLabel).attrs({})<{
  $dynamicColor: string;
}>`
  color: ${({ $dynamicColor }) => $dynamicColor} !important;
  font-family: bold !important;
  font-size: 16px !important;
  padding-top: 34px !important;
`;

export const StyledIndicatorSectionDiv = styled.div`
  padding-top: 34px !important;
`;

export const StyledFormSecondaryLabelClinical = styled(FormLabel).attrs({})<{
  $paddingLeft: number;
}>`
  color: ${({ theme }) => theme.greyMedium} !important;
  font-family: light !important;
  font-size: 14px !important;
  padding-left: ${({ $paddingLeft }) => $paddingLeft}px !important;
`;

export const StyledFormCodeContentClinical = styled(FormLabel).attrs({})<{
  $shouldBold: boolean;
}>`
  color: ${({ theme }) => theme.greyBase} !important;
  font-family: ${({ $shouldBold }) =>
    $shouldBold ? "bold" : "light"} !important;
  font-size: 16px !important;
  padding-top: 9px !important;
`;

export const IndicatorFieldGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 415px) repeat(3, 72px);
  grid-column-gap: 15px;
  grid-row-gap: 32px;
`;

export const StyledBox = styled(Box)`
  background-color: ${({ theme }) => theme.patientSummaryBanner};
  margin: 6px 0;
  border-radius: 8px;
  /* 32px inset from the aqua panel edge. */
  padding: 0 32px;
  box-sizing: border-box;
`;

export const StyledRedSpan = styled.span`
  color: ${({ theme }) => theme.redDark};
`;

export const StyledFormLabelIndication = styled(FormLabel)`
  color: ${({ theme }) => theme.blueDark};
  font-family: light;
  line-height: inherit;
  font-size: 13px;
`;

export const PreFlightCheckButtons = styled.div`
  display: flex;
  position: absolute;
  justify-content: space-between;
  margin-top: 20px;
  background-color: ${({ theme }) => theme.neutral50};
  padding: 20px;
  pointer-events: all !important;
  width: calc(100% - 32px);
  bottom: 0;
  left: 0;
  z-index: 1000;

  ${StyledButton} {
    height: 48px !important;
  }

  @media (max-width: 600px) {
    padding: 10px;
    button {
      width: fit-content !important;
      padding: 4px 24px !important;
      margin: 5px;
    }
  }
`;

export const PrintTypo = styled(Typography)`
  padding-left: 3rem;
  color: ${({ theme }) => theme.black};
  font-size: 14px;
`;

export const FormLabelConfirmation = styled(FormLabel)`
  color: ${({ theme }) => theme.black};
  font-size: 15px;
  font-family: light;
`;

export const BoldText = styled.span`
  font-weight: bold;
`;

export const OrderDetailsContent = styled.div`
  @media print {
    div {
      break-inside: avoid;
    }
    .icd-codes-grid {
      break-inside: auto;
    }
  }
  && {
    .gender-form-control {
      border-bottom: ${({ theme }) =>
        `1px dashed ${theme.greyLight}`} !important;
    }

    .MuiInput-root:before {
      border-bottom: ${({ theme }) =>
        `1px dashed ${theme.greyLight}`} !important;
    }

    .MuiAutocomplete-input {
      color: ${({ theme }) => theme.black} !important;
    }

    .Mui-focused {
      color: ${({ theme }) => theme.black} !important;
    }

    min-width: 120px;
    color: ${({ theme }) => theme.black} !important;
    font-family: light;

    .MuiInput-input {
      font-family: light;
      font-size: 1rem;
    }

    .MuiInputLabel-root {
      color: ${({ theme }) => theme.blueDark} !important;
      font-family: light;
      line-height: inherit;
    }

    .MuiInput-root:after {
      border-bottom: ${({ theme }) =>
        `1px solid ${theme.greyLight}`} !important;
    }

    ${requisitionFieldErrorStyles}
  }
  width: 90%;
  margin: 20px auto;
  user-select: text;
`;

export const ViewOnlyOrderDetailsContent = styled(OrderDetailsContent)`
  pointer-events: none;
`;

export const NewReqActionCellWrapper = styled.div`
  padding: 10px 0px;
  .print-wrapper {
    display: none;
  }
`;

export const NewReqActionCellGridWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const NewReqActionCellButtonGrid = styled.div`
  display: grid;
  grid-gap: 10px;
  grid-template-columns: 65px 65px;
  width: fit-content !important;
`;

export const TestOrderStack = styled.div`
  display: inline-flex;
  flex-direction: column;
  white-space: nowrap;
  color: ${({ theme }) => theme.greyDark};
`;

export const TestOrderPrimary = styled.div`
  font-family: light;
  font-size: 14px;
  line-height: 20px;
`;

export const TestOrderSecondary = styled.div`
  ${testOrderSecondaryCss}
`;

export const NewReqOrderInfoContainer = styled.div`
  margin-bottom: 20px;
`;

export const NewReqOrderIdWrapper = styled.div`
  color: ${({ theme }) => theme.blueDark};
  font-size: 16px;
  line-height: 28px;
`;

export const NewReqPrintSpinnerWrapper = styled.div`
  text-align: center;
  img {
    width: 25px;
  }
`;

export const NewReqActionCellButton = styled.button`
  display: flex;
  cursor: pointer;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 16px;
  gap: 10px;
  border: 2px solid ${({ theme }) => theme.redMedium} !important;
  width: 62px;
  height: 32px;
  background: white;
  border-radius: 30px;
  flex: none;
  order: 0;
  flex-grow: 0;
  font-weight: 700;
  font-size: 11px;
  line-height: 14px;
  /* identical to box height */

  align-items: center;
  text-align: center;
  letter-spacing: 0.01em;
  font-variant: small-caps;

  color: ${({ theme }) => theme.redMedium};

  :hover {
    background: ${({ theme }) => theme.redMedium};
    color: white;
  }
`;

export const NewReqRequiredMessage = styled.div<{ $isError: boolean }>`
  background: ${({ theme, $isError }) =>
    $isError ? theme.redPastel : theme.blueSubtle};
  border-left: 3px solid
    ${({ theme, $isError }) => ($isError ? theme.redDark : theme.blueDark)};
  padding: 12px 20px;
  margin-bottom: 10px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  white-space: pre-wrap;
  svg {
    margin-right: 10px;
  }
`;

export const NewReqNoteMessage = styled.div<{ $isError: boolean }>`
  background: white;
  border-left: 3px solid
    ${({ theme, $isError }) => ($isError ? theme.redDark : theme.blueDark)};
  padding: 12px 20px;
  margin-bottom: 10px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  white-space: pre-wrap;
  width: max-content;
  svg {
    margin-right: 10px;
  }

  p {
    display: inline;
  }
`;

export const NewReqPrintHeader = styled.div`
  border-radius: 8px;
  color: ${({ theme }) => theme.blueDark};
`;
export const PrintInfographic = styled.img.attrs({
  src: `${Illustration}`,
})`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const NewReqInsuranceHeader = styled.div`
  font-style: normal;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  line-height: 24px;
  color: ${({ theme }) => theme.black};
  margin-top: 15px;
  margin-bottom: 5px;
`;

export const NewReqSecondaryInsuranceWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 25px;

  ${NewReqInsuranceHeader} {
    color: ${({ theme }) => theme.greyDark};

    span {
      color: ${({ theme }) => theme.redMedium};
    }
  }
`;

export const NewReqSecondaryButton = styled.div`
  color: var(--Secondary-Grey-Dark, #433f3f);
  text-align: center;
  max-width: 265px;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  font-variant: all-small-caps;
  letter-spacing: 0.18px;
  display: flex;
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 30px;
  border: 1px solid ${({ theme }) => theme.neutral500};
  cursor: pointer;
  margin-top: 20px;
  margin-bottom: 20px;
  :hover {
    opacity: 0.7;
  }
`;

export const NewReqSecondaryInsuranceLabel = styled.div`
  display: grid;
  grid-template-columns: max-content min-content;
  align-items: center;
  grid-gap: 5px;
  margin: 10px 0px;
  cursor: pointer;

  span {
    color: ${({ theme }) => theme.redMedium};
  }

  svg {
    color: ${({ theme }) => theme.redMedium};
    margin-top: 12px;
    width: 22px;
    height: 22px;
  }
`;

export const NewReqInsurancePadding = styled.div`
  padding-left: 30px;
  padding-right: 5px;
  margin-top: 10px;

  ${".MuiFormLabel-root"} {
    font-size: 16px !important;
  }
`;

export const NewReqInsuranceSheetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 8px;
  background: ${({ theme }) => theme.blueSubtle};
  border: 1px solid ${({ theme }) => theme.blueLight};
  border-radius: 6px;
  margin-left: 25px;
`;

export const NewReqInsuranceSheetText = styled.div`
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.blueDark};
  padding-left: 40px;
  line-height: 24px;
  svg {
    margin-right: 8px;
    margin-left: -28px;
    margin-bottom: -4px;
    margin-top: 12px;
  }
  ul {
    margin: 0px;
  }
  li {
    padding-top: 3px;
  }
`;

export const NewReqEmailHelper = styled.div`
  color: ${({ theme }) => theme.blueDark};
  font-family: light;
  line-height: inherit;
  font-size: 12px;
  svg {
    width: 15px;
    margin-bottom: -8px;
  }
`;

export const NewReqTypographyLabel = styled.div`
  p {
    display: inline;
  }
`;

/** Medium clinical option copy; use <strong>/<em> for weight/style. */
export const OptionLabel = styled.span<{
  $tone?: "default" | "muted" | "dark" | "alert" | "danger";
}>`
  font-size: 16px;
  line-height: 24px;
  font-family: light;
  font-weight: 400;
  color: ${({ theme, $tone = "default" }) => {
    switch ($tone) {
      case "muted":
        return theme.greyMedium;
      case "dark":
        return theme.greyDark;
      case "alert":
        return theme.textAlert;
      case "danger":
        return theme.redDark;
      case "default":
        return theme.blueDark;
      default: {
        const exhaustiveCheck: never = $tone;
        return exhaustiveCheck;
      }
    }
  }};

  strong {
    font-family: bold;
    font-weight: 700;
  }

  em {
    font-family: light;
    font-style: italic;
  }

  small {
    font-size: 14px;
    line-height: 20px;
  }
`;

export const OptionGenes = styled.span`
  display: block;
  font-size: 16px;
  line-height: 24px;
  font-family: light;
  font-style: italic;
  color: ${({ theme }) => theme.greyMedium};
`;

export const HelperCopy = styled.span<{ $size?: "xsmall" | "small" }>`
  font-size: ${({ $size = "xsmall" }) => ($size === "small" ? "14px" : "12px")};
  line-height: ${({ $size = "xsmall" }) =>
    $size === "small" ? "20px" : "18px"};
  font-family: light;
  font-weight: 400;
  color: ${({ theme }) => theme.greyMedium};

  strong {
    font-family: bold;
    font-weight: 700;
  }
`;

export const SummaryLabel = styled.span`
  font-size: 12px;
  line-height: 18px;
  font-family: light;
  font-weight: 400;
  color: ${({ theme }) => theme.greyMedium};
`;

export const SummaryValue = styled.span`
  font-size: 12px;
  line-height: 18px;
  font-family: bold;
  font-weight: 700;
  color: ${({ theme }) => theme.brandTeal};
`;

export const SectionTitleText = styled.span`
  font-size: 20px;
  line-height: 28px;
  font-family: bold;
  font-weight: 700;
  color: ${({ theme }) => theme.black};

  strong {
    font-family: bold;
    font-weight: 700;
  }
`;

export const NewReqBillingRadioTreatmentNotesAccordionWrapper = styled.div<{
  preflight: boolean;
}>`
  svg {
    width: 25px;
    height: 20px;
    color: ${({ theme }) => theme.redMedium};
  }

  .accordion-arrow-icon {
    display: ${({ preflight }) => (preflight ? "none" : "block")};
  }

  .MuiAccordionDetails-root {
    padding: 8px 16px 16px 0px;
  }

  .MuiAccordionSummary-root {
    padding: 0px 16px 0px 0px;
  }
`;

export const NewReqTreatmentNotesInfoIcon = styled(InfoIcon)`
  && {
    margin-top: 2px;
    margin-left: 5px;
    width: 22px;
    height: 22px;
    color: ${({ theme }) => theme.redMedium};
    pointer-events: all !important;
  }
`;

export const NewReqBillingRadioTreatmentNotesWrapper = styled.div<{
  deviceType: DeviceType;
}>`
  background: white;
  max-height: ${({ deviceType }) =>
    deviceType === "mobile" ? "80%" : undefined};
  overflow: ${({ deviceType }) =>
    deviceType === "mobile" ? "scroll" : undefined};

  .MuiTextField-root {
    position: inherit;
  }

  .MuiInputBase-root {
    box-sizing: border-box;
    background: ${({ theme }) => theme.white};
    border-radius: 12px;
  }

  .Mui-focused {
    color: ${({ theme }) => theme.black} !important;
    background: ${({ theme }) => theme.white};
  }

  .Mui-disabled {
    color: ${({ theme }) => theme.neutral700} !important;
    -webkit-text-fill-color: ${({ theme }) => theme.neutral700} !important;
  }

  ${StyledButton} {
    margin-top: 20px;
  }

  ${UserReportGenerateNotesModalNPIMessage} {
    margin: 10px 0px;
  }
`;

export const NewReqTooltipContent = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  width: 100%;
  padding: 5px 5px;
  color: ${({ theme }) => theme.greyDark} !important;
  background-color: ${({ theme }) => theme.white} !important;

  span {
    font-weight: 800;
  }
`;

export const NewReqTooltip = styled(Tooltip)``;

export const NewReqTreatmentNotesTooltip = styled(Tooltip).attrs({
  PopperProps: {
    sx: {
      "& .MuiTooltip-tooltip": {
        backgroundColor: "white",
        boxShadow: "0px 10px 20px rgb(0 0 0 / 10%)",
        zIndex: 100000,
      },
    },
  },
})``;

export const NewReqTreatmentNotesMessage = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.greyMedium};
  margin-top: -10px;
  margin-bottom: 5px;
`;

export const ConfirmationWrapper = styled.div`
  && {
    .MuiInput-root:after {
      border-bottom: none !important;
    }
    .MuiInput-root:before {
      border-bottom: none !important;
    }
    .MuiInputLabel-root {
      color: ${({ theme }) => theme.greyMedium} !important;
      font-size: 18px;
      font-family: light;
      line-height: inherit;
    }
    input.Mui-disabled {
      color: ${({ theme }) => theme.black} !important;
      -webkit-text-fill-color: ${({ theme }) => theme.black} !important;
    }
  }

  width: 100%;
  ${OrderDetailsContent},
  ${ViewOnlyOrderDetailsContent} {
    display: none;
  }
`;

export const BoxConfirmation = styled(Box)`
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.white};
  svg {
    box-shadow: 0px 4.04241px 7.41109px rgba(0, 0, 0, 0.04);
    border-radius: 4px;
  }
`;

export const NewReqConfirmationButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  background-color: ${({ theme }) => theme.neutral50};
  padding: 20px;
  width: 100%;
  margin-left: -20px;
  margin-bottom: -20px;

  button {
    padding: 5px 15px !important;
  }

  ${StyledButton} {
    height: 48px !important;
  }

  @media (max-width: 600px) {
    button {
      width: fit-content !important;
      padding: 4px 24px !important;
    }
  }
`;

export const NewReqConfirmationMessage = styled.div`
  display: grid;
  grid-template-rows: auto auto auto;
  align-items: center;
  padding: 16px;
  background: ${({ theme }) => theme.blueSubtle};
  border: 1px solid ${({ theme }) => theme.blueLight};
  border-radius: 6px;
  width: 90%;

  .new-req-confirmation-message-insurance {
    margin-left: 30px;
  }
`;

export const NewReqBillingConfirmationList = styled.ul`
  display: block;
  margin: 0px 0px 0px 5px;
`;

export const NewReqConfirmationMessageText = styled.div`
  p {
    display: inline;
  }
  .new-req-confirmation-message {
    margin-left: 15px;
    svg {
      color: ${({ theme }) => theme.blueDark};
      margin-right: 10px;
      margin-bottom: -2px;
    }
  }
`;

export const FetalAntigenPrintoutMessage = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  padding: 16px;
  background: ${({ theme }) => theme.blueSubtle};
  border: 1px solid ${({ theme }) => theme.blueLight};
  border-radius: 6px;
  width: 90%;
`;

export const FetalAntigenFileItems = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: auto;
  grid-column-gap: 12px;
  width: 100%;
`;

export const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
  if (e.key === `"` || e.key === `"`) {
    e.preventDefault();
  }
};

/** Patient recap band; negative margins cancel DialogContent padding. */
export const NewReqPatientSummaryBannerWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  height: 52px;
  margin: 0 -24px 16px;
  padding: 0 24px;
  background-color: ${({ theme }) => theme.patientSummaryBanner};
  border-top: 1px solid ${({ theme }) => theme.darkAqua20};
  border-bottom: 1px solid ${({ theme }) => theme.darkAqua20};

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    margin: 0 -16px 16px;
    padding: 16px;
    row-gap: 8px;
  }
`;

export const NewReqPatientSummaryColumn = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  column-gap: 13px;
  row-gap: 2px;
  align-content: center;
  min-width: 0;
  /* Keep long unbroken names inside the band. */
  overflow-wrap: anywhere;
`;
